import { useMemo, useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import StepShell from "@/components/session/StepShell";
import RevisionStep from "@/components/session/RevisionStep";
import TheoryStep from "@/components/session/TheoryStep";
import ActivitiesStep from "@/components/session/ActivitiesStep";
import ThoughtRecordStep from "@/components/session/ThoughtRecordStep";
import ClassifyStep from "@/components/session/ClassifyStep";
import TechniquesStep from "@/components/session/TechniquesStep";
import GenericQuizStep from "@/components/session/GenericQuizStep";
import DoubleParameterStep from "@/components/session/DoubleParameterStep";
import BeliefListsStep from "@/components/session/BeliefListsStep";
import ActionPlanStep from "@/components/session/ActionPlanStep";
import FinalReviewStep from "@/components/session/FinalReviewStep";
import AppSatisfactionStep from "@/components/session/AppSatisfactionStep";
import QuestionnaireStep from "@/components/session/QuestionnaireStep";
import AbcReframeStep from "@/components/session/AbcReframeStep";
import AbcIdentifyStep from "@/components/session/AbcIdentifyStep";
import SummaryStep from "@/components/session/SummaryStep";
import { SESSIONS, SESSION_SECTIONS } from "@/data/sessions";
import { ALL_QUESTIONNAIRES } from "@/data/questionnaires";
import { saveProgress, getSessionProgress } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { HeartHandshake, ChevronRight, RotateCcw, ClipboardCheck, BookOpen, Brain, Sparkles, Target, Wind, Heart, Leaf, ShieldCheck, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const SECTION_ICONS = {
  review: RefreshCw,
  clipboard: ClipboardCheck,
  book: BookOpen,
  brain: Brain,
  sparkles: Sparkles,
  target: Target,
  wind: Wind,
  heart: Heart,
  leaf: Leaf,
  shield: ShieldCheck,
};

export default function GenericSession() {
  const { num } = useParams();
  const navigate = useNavigate();
  const sessionNumber = Number(num);
  const session = SESSIONS[sessionNumber];
  const [step, setStep] = useState(0);
  const [evalIdx, setEvalIdx] = useState(0);

  const sections = SESSION_SECTIONS[sessionNumber];

  // Build the runtime `steps` array. Inserts `sectionIntro` pseudo-steps at the
  // boundary of each section and attaches a `section` reference to every step
  // that belongs to a section (welcome + summary have no section).
  const steps = useMemo(() => {
    if (!session) return [];
    const base = [{ id: "welcome", kind: "welcome" }, ...session.steps];
    if (!sections || sections.length === 0) return base;

    // Map from stepId → section object
    const stepIdToSection = {};
    for (const sec of sections) {
      for (const sid of sec.stepIds) stepIdToSection[sid] = sec;
    }

    const out = [];
    let lastSectionId = null;
    for (const s of base) {
      const sec = stepIdToSection[s.id] || null;
      if (sec && sec.id !== lastSectionId) {
        out.push({ id: `intro-${sec.id}`, kind: "sectionIntro", section: sec });
        lastSectionId = sec.id;
      }
      out.push(sec ? { ...s, section: sec } : s);
    }
    return out;
  }, [session, sections]);

  // Load resume-step from backend on mount
  const [resumeAvailable, setResumeAvailable] = useState(null); // null | number
  useEffect(() => {
    if (!session) return;
    getSessionProgress(sessionNumber)
      .then(d => {
        if (d?.current_step && d.current_step > 0 && d.current_step < steps.length) {
          setResumeAvailable(d.current_step);
        }
      })
      .catch(() => {});
  }, [session, sessionNumber, steps.length]);

  // Persist current step (debounced trivially by only saving on change)
  useEffect(() => {
    if (!session || step === 0) return;
    saveProgress(sessionNumber, "_current_step", false, { step_index: step }).catch(() => {});
  }, [step, session, sessionNumber]);

  if (!session) {
    return (
      <div className="min-h-screen grid place-items-center bg-[var(--ha-bg)]">
        <p className="text-[var(--ha-muted)]">Sesión no encontrada.</p>
      </div>
    );
  }
  const total = steps.length;
  const current = steps[step];
  const next = () => { setEvalIdx(0); setStep(s => Math.min(s + 1, total - 1)); };
  const prev = () => { setEvalIdx(0); setStep(s => Math.max(s - 1, 0)); };

  const renderStep = () => {
    switch (current.kind) {
      case "welcome":
        return (
          <Card className="rounded-3xl border-[var(--ha-border)] bg-white">
            <CardContent className="p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-2xl bg-[var(--ha-primary)]/10 grid place-items-center">
                  <HeartHandshake className="w-5 h-5 text-[var(--ha-primary)]" strokeWidth={1.75} />
                </div>
                <p className="text-xs uppercase tracking-[0.18em] text-[var(--ha-muted)] font-semibold">Sesión {String(sessionNumber).padStart(2,"0")}</p>
              </div>
              <p className="text-[var(--ha-muted)] leading-relaxed">{session.intro}</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Button data-testid="start-session-btn" onClick={next} className="rounded-full bg-[var(--ha-primary)] hover:bg-[var(--ha-primary-hover)] text-white h-11 px-6">
                  Comenzar sesión <ChevronRight className="w-4 h-4 ml-1" strokeWidth={1.75} />
                </Button>
                {resumeAvailable && (
                  <Button
                    data-testid="resume-session-btn"
                    variant="outline"
                    onClick={() => { setStep(resumeAvailable); toast.success(`Retomando en el paso ${resumeAvailable + 1}`); }}
                    className="rounded-full border-[var(--ha-primary)] text-[var(--ha-primary)] h-11 px-6"
                  >
                    <RotateCcw className="w-4 h-4 mr-1.5" strokeWidth={1.75} /> Continuar donde lo dejé (paso {resumeAvailable + 1})
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        );
      case "sectionIntro": {
        const sec = current.section;
        const Icon = SECTION_ICONS[sec.intro.iconKey] || Sparkles;
        return (
          <Card className="rounded-3xl border-[var(--ha-border)] bg-white overflow-hidden" data-testid={`section-intro-${sec.id}`}>
            <div className="h-2 bg-gradient-to-r from-[var(--ha-primary)] via-[var(--ha-accent)] to-[var(--ha-secondary)]" />
            <CardContent className="p-6 sm:p-10">
              <div className="flex items-start gap-4">
                <div className="h-14 w-14 rounded-2xl bg-[var(--ha-primary)]/10 grid place-items-center shrink-0">
                  <Icon className="w-7 h-7 text-[var(--ha-primary)]" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[var(--ha-text)]">{sec.intro.title}</h1>
                  <p className="mt-2 text-[var(--ha-muted)]">{sec.intro.subtitle}</p>
                </div>
              </div>
              <p className="mt-6 text-[var(--ha-text)] leading-relaxed">{sec.intro.description}</p>
              <ul className="mt-6 space-y-2">
                {sec.intro.bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-[var(--ha-text)]">
                    <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[var(--ha-primary)] shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex justify-end">
                <Button
                  data-testid={`section-intro-continue-${sec.id}`}
                  onClick={next}
                  className="rounded-full bg-[var(--ha-primary)] hover:bg-[var(--ha-primary-hover)] text-white h-11 px-6"
                >
                  {sec.intro.buttonText} <ChevronRight className="w-4 h-4 ml-1" strokeWidth={1.75} />
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      }
      case "revision":
        return <RevisionStep sessionNumber={sessionNumber} onDone={next} />;
      case "theory":
        return <TheoryStep sections={current.sections} onDone={next} />;
      case "activities":
        return <ActivitiesStep sessionNumber={sessionNumber} onDone={next} />;
      case "thoughtRecord":
        return <ThoughtRecordStep sessionNumber={sessionNumber} subtitle={current.subtitle} onDone={next} />;
      case "classify":
        return <ClassifyStep {...current} sessionNumber={sessionNumber} onDone={next} />;
      case "techniques":
        return <TechniquesStep techniques={current.techniques} onDone={next} />;
      case "quiz":
        return <GenericQuizStep {...current} onDone={next} />;
      case "doubleParam":
        return <DoubleParameterStep sessionNumber={sessionNumber} intro={current.intro} onDone={next} />;
      case "beliefLists":
        return <BeliefListsStep sessionNumber={sessionNumber} onDone={next} />;
      case "actionPlan":
        return <ActionPlanStep sessionNumber={sessionNumber} onDone={next} />;
      case "finalReview":
        return <FinalReviewStep sessionNumber={sessionNumber} onDone={next} />;
      case "appSatisfaction":
        return <AppSatisfactionStep sessionNumber={sessionNumber} onDone={next} />;
      case "abcReframe":
        return <AbcReframeStep onDone={next} scenariosOverride={current.scenarios} />;
      case "abcIdentify":
        return <AbcIdentifyStep onDone={next} scenariosOverride={current.scenarios} quizIdOverride={current.quizId} />;
      case "summary":
        return <SummaryStep />;
      case "eval": {
        const kind = current.subset[evalIdx];
        const q = ALL_QUESTIONNAIRES.find(x => x.kind === kind);
        const isLast = evalIdx === current.subset.length - 1;
        const phase = sessionNumber === 8 ? "final" : "baseline";
        return (
          <div key={kind}>
            <p className="text-xs uppercase tracking-[0.16em] text-[var(--ha-muted)] font-bold mb-1">
              Cuestionario {evalIdx + 1} de {current.subset.length}
            </p>
            <QuestionnaireStep
              questionnaire={q}
              phase={phase}
              onDone={() => {
                if (isLast) next();
                else setEvalIdx(evalIdx + 1);
              }}
            />
          </div>
        );
      }
      default:
        return null;
    }
  };

  const title = current.title || (current.kind === "welcome" ? session.title : undefined);
  const subtitle = current.subtitle;

  return (
    <StepShell
      current={step + 1}
      total={total}
      sessionNumber={sessionNumber}
      section={current.section || null}
      title={title}
      subtitle={subtitle}
      onBack={step > 0 ? prev : null}
    >
      {renderStep()}
    </StepShell>
  );
}
